import type { CalendarPlanSlot, CalendarSlotIdea, IdeaReference } from "@/types/contentCalendar.type";

/**
 * Reopening retains the reviewed idea's ID; generating replacement directions
 * creates new idea IDs. Use that persisted identity so a reload resumes the
 * same manual draft without treating new alternatives as already chosen.
 */
export function reopenedConceptIdea(slot: CalendarPlanSlot): CalendarSlotIdea | null {
  const decision = slot.reviewDecisions?.[0];
  if (slot.status !== "IDEATION" || decision?.decision !== "DECLINED") return null;
  const reviewedId = decision.ideaId || decision.ideaSnapshot?.id;
  if (!reviewedId || (decision.ideaSnapshot?.id && decision.ideaSnapshot.id !== reviewedId)) return null;
  if (slot.ideas?.some(idea => idea.status === "ACCEPTED")) return null;
  return slot.ideas?.find(idea => idea.id === reviewedId && idea.status === "PROPOSED") ?? null;
}

export function conceptDraftState(slot: CalendarPlanSlot, canEdit: boolean, historyReady: boolean) {
  const reopenedIdea = reopenedConceptIdea(slot);
  const idea = slot.status === "SIGNED_OFF"
    ? slot.ideas?.find(candidate => candidate.status === "ACCEPTED") ?? null
    : reopenedIdea;
  const editable = canEdit && !!idea && (!reopenedIdea || historyReady);
  return {
    idea,
    reopenedIdea,
    editable,
    // Reopening or saving a revision never substitutes for internal acceptance.
    sendable: editable && slot.status === "SIGNED_OFF" && !!idea,
  };
}

export type ConceptReviewAudience = "team" | "client" | "preview";
export type ConceptReviewAuthorType = "TEAM" | "CLIENT";
export type ConceptReviewMessageVisibility = "SHARED" | "INTERNAL";
export type ConceptReviewDecisionValue = "APPROVED" | "CHANGES_REQUESTED" | "DECLINED";

/** A message is pinned to the immutable concept version it was written against. */
export interface ConceptReviewMessage {
  id: string;
  version: number;
  authorType: ConceptReviewAuthorType;
  authorName: string;
  body: string;
  createdAt: string;
  visibility?: ConceptReviewMessageVisibility;
}

export interface ConceptReviewDecision {
  decision: ConceptReviewDecisionValue;
  reason: string | null;
  clientName: string;
  decidedAt: string;
}

/**
 * One frozen concept proposal. Drafts have `sharedAt: null`; client surfaces
 * receive shared versions only. Copy and reference belong to the same snapshot.
 */
export interface ConceptReviewVersion {
  id: string;
  version: number;
  sharedAt: string | null;
  title: string;
  synopsis: string | null;
  objectiveClaim: string | null;
  /** Every hook option, in order — the proposal lists them all. */
  hooks: string[];
  scheduledAt: string;
  pillarName: string | null;
  format: string;
  reference: IdeaReference | null;
  approved?: boolean;
  decision: ConceptReviewDecision | null;
  discussion: ConceptReviewMessage[];
}

export function orderedConceptVersions(
  versions: readonly ConceptReviewVersion[],
): ConceptReviewVersion[] {
  return [...versions].sort((a, b) => b.version - a.version);
}

export function latestSharedConceptVersion(
  versions: readonly ConceptReviewVersion[],
): ConceptReviewVersion | null {
  return orderedConceptVersions(versions).find((version) => version.sharedAt !== null) ?? null;
}

export function sharedDiscussionFor(
  version: ConceptReviewVersion,
  audience: ConceptReviewAudience,
): ConceptReviewMessage[] {
  return version.discussion.filter(
    (message) =>
      message.version === version.version &&
      (audience === "team" || message.visibility !== "INTERNAL"),
  );
}

export function isApprovedDecision(decision: ConceptReviewDecision | null): boolean {
  return decision?.decision === "APPROVED";
}
